<template>
    <v-card height="100%">
        <v-container fluid fill-height ma-0 pa-0>
            <v-layout column>
                <v-flex shrink>
                    <v-toolbar flat dense text>
                        <v-autocomplete
                            :label="$t('Suchen')"
                            hide-details
                            :items="flattendTree"
                            v-model="computedSelectedSearch"
                            item-key="treeId"
                            item-value="treeId"
                            return-object
                            :no-data-text="$t('Keine Daten')"
                            :no-results-text="$t('Kein Ergebnis')"
                            :filter="filter"
                            :search-input.sync="computedSearch"
                            :loading="searchDebounce !== null"
                            :no-filter="searchDebounce !== null"
                            :disabled="flattendTree.length === 0"
                            @focus="resetSearchSelection"
                        >
                            <template v-slot:selection="{ item, selected }">
                                <v-chip :value="!search" color="primary" dark>
                                    {{ item.name || item.displayName }}
                                </v-chip>
                            </template>
                            <template v-slot:item="{ item }">
                                <v-list-item-action>
                                    <v-icon medium :color="iconColor(item)">{{ icon(item, false) }}</v-icon>
                                </v-list-item-action>
                                <v-list-item-content>
                                    <v-list-item-title>
                                        {{ item.name || item.displayName }}
                                    </v-list-item-title>
                                    <v-list-item-subtitle>
                                        {{ pathToSearchItem(item) }}
                                    </v-list-item-subtitle>
                                </v-list-item-content>
                            </template>
                        </v-autocomplete>
                        <template v-slot:extension>
                            <v-flex shrink>
                                <UsersConfiguration />
                            </v-flex>
                            <v-flex shrink v-show="!showList">
                                <v-tooltip bottom>
                                    <template v-slot:activator="{ on }">
                                        <v-btn v-on="on" icon @click="showExcelView" :disabled="!permissions.excel">
                                            <v-icon>mdi-file-excel</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>{{ $t('Excel Nutzerverwaltung') }}</span>
                                </v-tooltip>
                            </v-flex>
                            <v-flex shrink v-show="selectable && !showExcel">
                                <v-tooltip bottom>
                                    <template v-slot:activator="{ on }">
                                        <v-btn v-on="on" icon @click="showListView">
                                            <v-icon>mdi-format-list-bulleted</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>{{ $t('Auswahlliste') }}</span>
                                </v-tooltip>
                            </v-flex>
                            <v-flex shrink v-show="!showList">
                                <v-btn icon @click="goUp" :disabled="!isSelected">
                                    <v-icon>mdi-folder-upload</v-icon>
                                </v-btn>
                            </v-flex>
                            <v-flex grow v-show="!showList">
                                <v-breadcrumbs :items="breadcrumbPath">
                                    <template v-slot:item="{ item }">
                                        <span
                                            :class="{
                                                pointer: item !== computedSelectedItem,
                                                'truncate-path': true
                                            }"
                                            @click="computedSelectedItem = [item]"
                                            >{{ item.name || item[getExplorerConfig.display] }}
                                        </span>
                                    </template>
                                </v-breadcrumbs>
                            </v-flex>
                        </template>
                    </v-toolbar>
                </v-flex>
                <v-flex grow>
                    <keep-alive>
                        <component
                            :is="contentManagerComponent"
                            :component="selectedComponent"
                            v-model="computedSelectedItemArray"
                            :search="searchConfirm"
                            @openTree="handleOpenTree"
                            :selected="value"
                            @selected="handleSelection"
                            @switchView="(value) => (showList = value)"
                            :selectable="selectable"
                            :permissions="permissions"
                            :tree="getTree"
                            :user="user"
                            :admin="admin"
                        >
                            <v-treeview
                                v-if="isShowTree"
                                :items="getTree"
                                activatable
                                :active.sync="computedSelectedItemArray"
                                return-object
                                :open.sync="openPath"
                                class="pt-2"
                                ref="tree"
                                item-key="treeId"
                                dense
                            >
                                <template v-slot:prepend="{ item, open }">
                                    <v-icon :color="iconColor(item)">
                                        {{ icon(item, open) }}
                                    </v-icon>
                                </template>
                                <template v-slot:label="{ item, active }">
                                    <v-layout :ref="item.treeId">
                                        <v-flex shrink @click.stop v-if="selectable && isUserOrGroup(item) === 'GROUP'" class="pa-0 ma-0">
                                            <v-badge :value="isChecked(item)" color="primary" dark overlap>
                                                <template v-slot:badge>
                                                    <span>{{ getSelectedItemIndex(item) }}</span>
                                                </template>
                                                <v-simple-checkbox :value="isChecked(item)" @input="handleCheck($event, item)" />
                                            </v-badge>
                                        </v-flex>
                                        <v-flex align-self-center>
                                            <span
                                                :class="{
                                                    'primary--text': active,
                                                    pointer: true,
                                                    'no-highlight': true,
                                                    'truncate-tree-item': true
                                                }"
                                            >
                                                {{ item.name || getDisplay(item) }}
                                            </span>
                                        </v-flex>
                                    </v-layout>
                                </template>
                            </v-treeview>
                        </component>
                    </keep-alive>
                </v-flex>
            </v-layout>
        </v-container>
    </v-card>
</template>
<script>
import UsersDesktopView from './Helpers/UsersDesktopView';
import UsersMobileView from './Helpers/UsersMobileView';
import UsersView from './Helpers/UsersView';
import UsersSelectionView from './Helpers/UsersSelectionView';
import Excel from '../Excel/ExcelUsers';
import UsersConfiguration from './Helpers/UsersConfiguration';
import KnowledgeContentCheckbox from '../../KnowledgeContent/Helpers/KnowledgeContentCheckbox';
import UsersMixin from '../Mixins/UserMixin';

export default {
    name: 'users',
    components: {
        UsersDesktopView,
        UsersMobileView,
        UsersView,
        KnowledgeContentCheckbox,
        UsersSelectionView,
        Excel,
        UsersConfiguration
    },
    mixins: [UsersMixin],
    data: () => ({
        isLoaded: false,
        showList: false,
        showExcel: false,
        selectedSearch: {},
        search: '',
        searchConfirm: '',
        searchDebounce: null,
        selectedItem: [],
        openPath: [],
        permissions: {
            excel: false,
            create: false,
            update: false,
            delete: false
        },
        isShowTree: false
    }),
    props: {
        selectable: {
            type: Boolean,
            default: false
        },
        value: {
            type: Array,
            default: () => []
        },
        user: {
            type: Boolean,
            default: false
        },
        admin: {
            type: Boolean,
            default: false
        }
    },
    watch: {
        user: {
            handler(val) {
                if (val) this.BUILD_USER_MAPS().then(() => (this.isShowTree = true));
            }
        },
        admin: {
            handler(val) {
                if (val) this.BUILD_ADMIN_MAPS().then(() => (this.isShowTree = true));
            }
        },
        selectedItem() {
            this.reset();
        }
    },
    computed: {
        computedSelectedSearch: {
            get() {
                return this.selectedSearch;
            },
            set(value) {
                this.selectedSearch = value;
                this.computedSelectedItem = value ? [value] : [];
            }
        },
        flattendTree() {
            if (this.getTree.length === 0) {
                return [];
            }
            const flatten = (nodes) => {
                return nodes.reduce((acc, curr) => {
                    if (curr.children && curr.children.length > 0) {
                        return [...acc, curr, ...flatten(curr.children)];
                    }
                    return [...acc, curr];
                }, []);
            };
            return [...this.getTree, ...flatten(this.getTree)].sort((a, b) => {
                const ta = a.name || a.displayName;
                const tb = b.name || b.displayName;
                return ta.localeCompare(tb, undefined, {
                    numeric: true,
                    sensitivity: 'base'
                });
            });
        },
        computedSearch: {
            get() {
                return this.search;
            },
            set(value) {
                this.search = value;
                if (this.searchDebounce) {
                    clearTimeout(this.searchDebounce);
                    this.searchDebounce = null;
                }
                if (value !== '') {
                    this.searchDebounce = setTimeout(
                        function () {
                            this.data.searchConfirm = this.search;
                            this.data.searchDebounce = null;
                        }.bind({ data: this, search: value }),
                        1500
                    );
                } else {
                    clearTimeout(this.searchDebounce);
                    this.searchDebounce = null;
                    this.searchConfirm = '';
                }
            }
        },
        getTree() {
            if (this.user) {
                return this.getUserTree;
            }
            if (this.admin) {
                return this.getAdminTree;
            }
            return [];
        },
        breadcrumbPath() {
            return this.getTreeItemPath(this.selectedItem[0]);
        },
        isSelected() {
            return this.selectedItem.length > 0;
        },
        hasParent() {
            return this.isSelected ? !!this.getParent(this.selectedItem[0]) : false;
        },
        computedSelectedItemArray: {
            get() {
                return this.selectedItem;
            },
            set(value) {
                this.selectedItem = value;
                if (value.length > 0 && !!value[0]) {
                    let path = this.getTreeItemPath(value[0]);
                    path.pop();
                    this.openPath = [...this.openPath, ...path];
                }
            }
        },
        computedSelectedItem: {
            get() {
                return this.selectedItem.length > 0 ? this.selectedItem[0] : {};
            },
            set(value) {
                this.selectedItem = value;
                if (value.length > 0 && !!value[0]) {
                    let path = this.getTreeItemPath(value[0]);
                    path.pop();
                    this.openPath = [...this.openPath, ...path];
                    this.$nextTick(() =>
                        this.$vuetify.goTo(this.$refs[value[0].treeId], {
                            container: this.$refs.tree,
                            offset: 110
                        })
                    );
                }
            }
        },
        contentManagerComponent() {
            if (this.showExcel) {
                return Excel;
            }
            if (this.showList) {
                return UsersSelectionView;
            }
            if (this.isLoaded) {
                if (this.$vuetify.breakpoint.xs) {
                    return UsersMobileView;
                }
                return UsersDesktopView;
            } else {
                if (this.$vuetify.breakpoint.xs) {
                    return UsersDesktopView;
                } else {
                    return UsersMobileView;
                }
            }
        },
        selectedComponent() {
            return UsersView;
        }
    },
    methods: {
        resetSearchSelection() {
            this.selectedSearch = {};
        },
        pathToSearchItem(item) {
            let path = this.getTreeItemPath(item);
            if (path.length > 0) {
                path.pop();
            }
            return path.length > 0 ? path.map(({ name }) => name).join(' / ') : this.$t('Stammverzeichnis');
        },
        filter(item) {
            if (!this.searchConfirm || this.searchConfirm.trim() === '') {
                return true;
            }
            if (this.searchConfirm === this.search) {
                let buildString = [...new Set(this.searchConfirm.split(' '))].filter((word) => word !== '').map((word) => word.toLowerCase());
                const text = Object.values(item)
                    .filter((data) => typeof data === 'string' || typeof data === 'number')
                    .map((data) => {
                        if (typeof data === 'number') {
                            return data.toString();
                        }
                        return data;
                    })
                    .join(' ')
                    .toLowerCase();
                return buildString.some((word) => text.includes(word));
            } else {
                return true;
            }
        },
        getSelectedItemIndex({ id }) {
            return this.value.findIndex((item) => item.id === id) + 1;
        },
        reset() {
            this.SET_VIEW({ component: '', type: '', data: {} });
        },
        showExcelView() {
            this.showExcel = !this.showExcel;
        },
        showListView() {
            this.showList = !this.showList;
        },
        isChecked({ id }) {
            return this.value.map((item) => item.id).includes(id);
        },
        handleSelection(value) {
            this.$emit('input', value);
        },
        handleCheck(event, item) {
            if (event) {
                this.$emit('input', [...this.value, item]);
            } else {
                this.$emit(
                    'input',
                    this.value.filter((val) => val.id !== item.id)
                );
            }
        },
        goUp() {
            if (this.hasParent) {
                if (this.isSelected) {
                    const selected = this.getParent(this.selectedItem[0]);
                    if (selected) {
                        this.computedSelectedItem = [selected];
                    }
                }
            } else {
                this.computedSelectedItem = [];
            }
        },
        handleOpenTree(value) {
            this.openPath = value;
        },
        iconColor(item) {
            if (this.isUserOrGroup(item) === 'USER') {
                return item.active ? 'success' : 'warning';
            }
            return '';
        },
        icon(item, open) {
            if (item.hasOwnProperty('name')) {
                return open && item.children && item.children.length > 0 ? 'mdi-account-multiple-outline' : 'mdi-account-multiple';
            }
            return 'mdi-account';
        }
    },
    mounted() {
        this.$nextTick(() => (this.isLoaded = true));
        this.SET_VIEW({ component: '', type: '', data: {} });
    },
    beforeMount() {
        this.validateEditorState({
            excel: ['EXCEL_UPLOAD'],
            create: this.user ? ['USER_MANAGEMENT'] : this.admin ? ['ADMIN_MANAGEMENT'] : false,
            update: this.user ? ['USER_MANAGEMENT'] : this.admin ? ['ADMIN_MANAGEMENT'] : false,
            delete: this.user ? ['USER_MANAGEMENT'] : this.admin ? ['ADMIN_MANAGEMENT'] : false
        }).then((result) => (this.permissions = result));
        if (this.user) this.BUILD_USER_MAPS().then(() => (this.isShowTree = true));
        if (this.admin) this.BUILD_ADMIN_MAPS().then(() => (this.isShowTree = true));
    }
};
</script>

<style scoped>
.truncate-tree-item {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.v-treeview {
    height: 100%;
    width: max-content;
}
.truncate-path {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 7vw;
}
.v-input--selection-controls {
    margin-top: 0;
}
</style>
